import { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { useLogin } from "@/features/authentication";

// Ameen hosted chat widget — embedded via the copy-paste bootstrap snippet
// (see EMBED.md), not the @tameeni/chatbot-ui npm component. The widget renders
// its own floating launcher and owns its DOM, so we never create a container.

// Travel product GUID from the applicationReference table in EMBED.md.
const AMEEN_APPLICATION_REFERENCE = "552909AA-597E-4D2B-59AD-08DB9343B484";

// The global command function exposed by the bootstrap. Before the real bundle
// loads it is a queue stub that buffers calls on `.q`; the bundle then replays
// them. Same call signature either way, so callers never wait.
type AmeenWidgetGlobal = ((command: string, arg?: unknown) => void) & {
  q?: unknown[];
};

declare global {
  interface Window {
    AmeenWidget?: AmeenWidgetGlobal;
  }
}

type ChatWidgetClientProps = {
  readonly locale: "ar" | "en";
  // Base Ameen URL (e.g. https://www.tameeni.com/ameen). The bootstrap script
  // path is derived from it; `apiUrl` for runtime calls is inferred by the
  // widget from its own <script src>.
  readonly apiUrl: string;
};

// Install the command queue and inject the hosted bootstrap script once.
// Idempotent: guards on both the global and the injected <script> tag, so
// React re-renders / fast-refresh never duplicate either.
function ensureAmeenScript(scriptSrc: string): AmeenWidgetGlobal {
  if (!window.AmeenWidget) {
    const queue = function (...args: unknown[]) {
      queue.q = queue.q || [];
      queue.q.push(args);
    } as AmeenWidgetGlobal;
    window.AmeenWidget = queue;
  }

  if (!document.querySelector("script[data-ameen-embed]")) {
    const script = document.createElement("script");
    script.async = true;
    script.src = scriptSrc;
    script.dataset.ameenEmbed = "1";
    document.head.appendChild(script);
  }

  return window.AmeenWidget;
}

export default function ChatWidgetClient({
  locale,
  apiUrl,
}: ChatWidgetClientProps) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const { signIn } = useLogin();
  // Only hand a token to the widget when the session is actually authenticated.
  // `user.access_token` can linger (e.g. expired session), so gate on
  // `isAuthenticated` to avoid sending a stale token when not authenticated.
  const accessToken = isAuthenticated ? user?.access_token : undefined;

  const scriptSrc = `${apiUrl.replace(/\/$/, "")}/api/widget/v1.js`;

  // Has `init` run yet? Gates the update effects so they don't fire pre-boot.
  const bootedRef = useRef(false);
  // Last token handed to the widget, so we skip redundant `updateToken` calls.
  const appliedTokenRef = useRef<string | null | undefined>(undefined);

  // Latest onAuthRequired handler. `init` captures onAuthRequired once, but
  // signIn is recreated each render — route through a ref so the widget always
  // calls the current handler, never a stale boot-time closure.
  const requestLoginRef = useRef<() => void>(() => {});
  requestLoginRef.current = () => {
    const currentUrl =
      typeof window !== "undefined"
        ? window.location.pathname + window.location.search
        : "/";
    signIn({ postLoginRedirectionUrl: currentUrl });
  };

  // Boot once, after the auth session settles (so we init with a resolved
  // token rather than the initial `undefined`).
  useEffect(() => {
    if (bootedRef.current) return;
    if (isLoading || !apiUrl) return;
    // Don't render the widget when embedded in an iframe.
    if (typeof window !== "undefined" && window.self !== window.top) return;

    ensureAmeenScript(scriptSrc)("init", {
      applicationReference: AMEEN_APPLICATION_REFERENCE,
      locale,
      accessToken, // omitted (undefined) when unauthenticated
      // Called by the widget when it needs an authenticated user. Delegates
      // through the ref to the current sign-in handler. In this app sign-in is
      // redirect-based, so we start the flow here; the fresh token reaches the
      // widget via the `updateToken` effect below once the user returns.
      onAuthRequired: () => requestLoginRef.current(),
    });

    bootedRef.current = true;
    appliedTokenRef.current = accessToken;
  }, [isLoading, accessToken, locale, apiUrl, scriptSrc]);

  // Push token rotations (sign-in / sign-out / refresh) into the live widget.
  useEffect(() => {
    if (!bootedRef.current || appliedTokenRef.current === accessToken) return;
    appliedTokenRef.current = accessToken;
    window.AmeenWidget?.("updateToken", accessToken ?? null);
  }, [accessToken]);

  // Push locale flips into the live widget.
  useEffect(() => {
    if (!bootedRef.current) return;
    window.AmeenWidget?.("setLocale", locale);
  }, [locale]);

  // The widget renders its own floating launcher and manages its own DOM.
  return null;
}
